import { useState } from 'react'
import { Formik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useCreateUser } from '@dhl/mdp-api'
import { Box, Card, H6 } from '@dhl-ui/components'

import { links } from '~/AppRoutes'
import { useNotificationMessage } from '~/common'
import { getErrorMessage } from '~/common/util/axiosUtil'
import { CancelDialog, EditUserOrganisationCard, UserForm } from '~/modules/users/components'
import {
  type BaseUser,
  convertToUser,
  createBaseUserValidationSchema,
  initialBaseUser,
  User,
} from '~/modules/users/model'
import { BackButton } from '~/temp-ds-component/BackButton'

export const UserAddPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotificationMessage()
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false)

  const [user, setUser] = useState<User>()

  const validationSchema = createBaseUserValidationSchema(t)

  const { mutateAsync: createUser } = useCreateUser()

  const onSubmit = (newUser: BaseUser) => {
    createUser(
      { data: newUser },
      {
        onSuccess: async (addedUser) => {
          if (addedUser) {
            setUser(convertToUser(addedUser))
            showSuccess(t('admin.adminUsersPage.userForm.createUserMessage'))
          }
        },
        onError: (error) => {
          const message = getErrorMessage(error, t('admin.usersPage.dangerZoneView.errorMessage'))
          showError(message)
        },
      }
    )
  }

  const onBack = (dirty: boolean) => {
    if (dirty) {
      setCancelDialogOpen(true)
    } else if (!user) {
      navigate(links.userListLink())
    } else {
      navigate(links.userDetailLink(user.id))
    }
  }

  return (
    <>
      <Formik initialValues={initialBaseUser} onSubmit={onSubmit} validateOnMount validationSchema={validationSchema}>
        {({ dirty }) => (
          <>
            <Box display="flex" justifyContent="space-between" pb={2} pl={1}>
              <BackButton onClick={() => onBack(dirty)}>{t('admin.billingUnitsPage.backButton')}</BackButton>
            </Box>
            {!user ? (
              <Card>
                <Card.Header title={t('admin.usersPage.profileView.newUser')} />
                <Card.Content>
                  <H6>{t('admin.usersPage.profileView.accountText')}</H6>
                  <UserForm onCancel={() => onBack(dirty)} />
                </Card.Content>
              </Card>
            ) : (
              <EditUserOrganisationCard user={user} onCancel={() => onBack(false)} onOrganisationChanged={() => {}} />
            )}
          </>
        )}
      </Formik>
      <CancelDialog open={cancelDialogOpen} onCancel={() => setCancelDialogOpen(false)} />
    </>
  )
}
